import { POSITION_BADGES } from 'config/portfolio'
import { ROUTES } from 'config/routes'
import { ProcessedV2Position } from 'types/subgraph'
import { formatNumber } from 'utils/formatCurrencyAmount'
import { formatPercentage } from 'utils/numbers'
import { formatDollarAmount } from 'utils/portfolio/formatDollar'
import { calculateV2PositionValue } from 'utils/positionHelpers'
import { Address } from 'viem'

import { PortfolioConnectorLines } from './PortfolioConnectorLines'
import { PortfolioSchemaRow } from './PortfolioSchemaRow'
import { PositionCTAs } from './PositionCTAs'
import { FormattedTokenBalance } from './TokenBalanceDisplay'
import { TokenPairLogo } from './TokenPairLogo'

interface V2PositionSimpleRowProps {
  position: ProcessedV2Position
  tokenPrices?: Record<Address, number>
}

export function V2PositionSimpleRow({ position, tokenPrices }: V2PositionSimpleRowProps) {
  const positionValue = calculateV2PositionValue(position, tokenPrices)
  const sharePercentage = parseFloat(position.shareOfPool)

  const token0AmountFormatted = formatNumber(position.token0Amount)
  const token1AmountFormatted = formatNumber(position.token1Amount)

  const token0Price = tokenPrices?.[position.token0.address.toLowerCase() as Address] || 0
  const token1Price = tokenPrices?.[position.token1.address.toLowerCase() as Address] || 0
  const token0ValueUSD = parseFloat(position.token0Amount) * token0Price
  const token1ValueUSD = parseFloat(position.token1Amount) * token1Price

  const addLiquidityCTA = {
    label: 'Add',
    to: ROUTES.liquidity.v2.add(position.token0.address, position.token1.address),
    variant: 'primary' as const,
  }

  const removeLiquidityCTA = {
    label: 'Remove',
    to: ROUTES.liquidity.v2.remove(position.token0.address, position.token1.address),
    variant: 'secondary' as const,
  }

  return (
    <PortfolioSchemaRow
      hasExpandableContent
      desktopExpandable={false}
      containerClassName="sm:border-b sm:border-b-kodiak-border-secondary sm:last:border-b-0"
      mobileClassName="p-0"
      desktopClassName="items-center py-3 px-6 gap-4"
      desktopStyle={{
        gridTemplateColumns: 'minmax(0, 1.55fr) minmax(0, 1fr) minmax(0, 0.8fr) minmax(0, 1fr) minmax(0, 0.8fr)',
      }}
      identity={{
        leading: <TokenPairLogo token0={position.token0} token1={position.token1} size="md" />,
        title: `${position.token0.symbol}-${position.token1.symbol}`,
        subtitle: '0.3%',
        badges: (
          <span
            className="px-1.5 sm:px-1.5 py-0.5 sm:py-1 rounded-xs text-[11px] font-medium self-start whitespace-nowrap"
            style={{ backgroundColor: POSITION_BADGES.v2.bgColor, color: POSITION_BADGES.v2.textColor }}
          >
            {POSITION_BADGES.v2.label}
          </span>
        ),
      }}
      mobile={{
        value: (
          <div className="text-kodiak-text-primary font-medium text-sm">
            {formatDollarAmount(positionValue.totalValue)}
          </div>
        ),
        rightTop: (
          <span
            className="px-1.5 py-0.5 rounded-xs text-[11px] font-medium"
            style={{ backgroundColor: POSITION_BADGES.v2.bgColor, color: POSITION_BADGES.v2.textColor }}
          >
            {POSITION_BADGES.v2.label}
          </span>
        ),
        rootClassName: 'p-3 flex items-center justify-between',
        leftClassName: 'flex items-center space-x-3',
        leftTextClassName: 'flex flex-col gap-0.5',
        titleClassName: 'font-semibold text-kodiak-text-primary text-sm',
        subtitleClassName: 'text-xs text-kodiak-text-secondary',
      }}
      desktop={{
        showExpandIcon: false,
        containerClassName: '',
        rowClassName: 'flex items-center space-x-3',
        textClassName: 'flex flex-col gap-1',
        titleClassName: 'flex items-center gap-2 font-medium text-kodiak-text-primary',
        subtitleClassName: 'text-xs text-kodiak-text-secondary',
        columns: (
          <>
            <div className="text-sm space-y-1">
              <FormattedTokenBalance
                value={`${token0AmountFormatted} ${position.token0.symbol} (${formatDollarAmount(
                  positionValue.token0Value
                )})`}
              />
              <FormattedTokenBalance
                value={`${token1AmountFormatted} ${position.token1.symbol} (${formatDollarAmount(
                  positionValue.token1Value
                )})`}
              />
            </div>

            <div className="text-kodiak-text-primary font-medium">{formatDollarAmount(positionValue.totalValue)}</div>

            <div>
              <div className="text-sm text-kodiak-text-primary font-medium">
                {formatPercentage(String(sharePercentage))}
              </div>
              <div className="text-xs text-kodiak-text-secondary">of pool</div>
            </div>

            <PositionCTAs primaryCTA={addLiquidityCTA} secondaryCTA={removeLiquidityCTA} className="justify-end" />
          </>
        ),
      }}
      renderExpanded={() => (
        <div className="bg-[#222f42] sm:hidden">
          <div className="relative pl-6 pr-3 py-2 space-y-2">
            <PortfolioConnectorLines
              verticalLeftClassName="left-3"
              verticalTop="8px"
              verticalBottom="calc(2.5rem + 0.5rem + 8px)"
              mobileTop="calc(0.5rem + 0.75rem)"
              showDesktop={false}
            />

            <div className="flex items-center gap-2 mb-2">
              <span
                className="px-1.5 py-0.5 rounded-xs text-[11px] font-medium"
                style={{ backgroundColor: POSITION_BADGES.v2.bgColor, color: POSITION_BADGES.v2.textColor }}
              >
                {POSITION_BADGES.v2.label}
              </span>
            </div>

            <div className="flex justify-between gap-2">
              <div>
                <div className="text-xs text-kodiak-text-primary mb-1">Token Balances</div>
                <div className="text-xs space-y-1">
                  <div>
                    <span className="text-kodiak-text-primary">{token0AmountFormatted}</span>{' '}
                    <span className="text-kodiak-text-secondary">
                      {position.token0.symbol} ({formatDollarAmount(token0ValueUSD)})
                    </span>
                  </div>
                  <div>
                    <span className="text-kodiak-text-primary">{token1AmountFormatted}</span>{' '}
                    <span className="text-kodiak-text-secondary">
                      {position.token1.symbol} ({formatDollarAmount(token1ValueUSD)})
                    </span>
                  </div>
                </div>
              </div>

              <div className="text-right">
                <div className="text-xs text-kodiak-text-primary mb-1">Pool Share</div>
                <div className="text-xs text-kodiak-text-secondary">{formatPercentage(String(sharePercentage))}</div>
              </div>
            </div>

            <PositionCTAs primaryCTA={addLiquidityCTA} secondaryCTA={removeLiquidityCTA} />
          </div>
        </div>
      )}
    />
  )
}
